/**
 * The last meetings between the two teams, newest first, each with that game's
 * own away and home sides, scores and date, the winner's side bold. Strings
 * and scores come ready from the server. ISSUE-988 WI-25.
 */
import type { ReactNode } from "react";

import { TeamMark } from "./TeamMark";
import type { typeMatchupTeamMark, typeMeeting } from "./matchupTypes";
import { GameSectionCard } from "./GameSectionCard";

const meetingScoreClass = (
  meeting: typeMeeting,
  side: "away" | "home",
): string =>
  meeting.winnerSide === side || meeting.winnerSide === "tie"
    ? "text-ink"
    : "text-ink-secondary";

export const HeadToHeadSection = ({
  title,
  meetings,
  away,
  home,
}: {
  title: string;
  meetings: readonly typeMeeting[];
  away?: typeMatchupTeamMark;
  home?: typeMatchupTeamMark;
}): ReactNode => {
  if (meetings.length === 0) {
    return null;
  }
  const logoFor = (abbr: string) => {
    const matches = [away, home].filter(
      (team) =>
        team !== undefined && (team.abbreviation ?? team.plateText) === abbr,
    );
    return matches.length === 1 ? (matches[0]?.logoUrl ?? null) : null;
  };
  return (
    <GameSectionCard
      testId="matchup-head-to-head"
      title={title}
      caption="Most recent first"
    >
      {meetings.map((meeting) => (
        <div
          key={meeting.key}
          data-testid="matchup-meeting"
          className="flex min-h-touch-target flex-col items-stretch gap-2 border-t border-hairline py-3 first:border-t-0 sm:flex-row sm:items-center sm:gap-3"
        >
          <span
            className="shrink-0 text-small text-ink-secondary tabular-nums sm:w-24"
            data-testid="matchup-meeting-date"
          >
            {meeting.dateLabel}
          </span>
          <span className="flex min-w-0 flex-1 items-center justify-center gap-2">
            <span className="flex min-w-0 flex-1 flex-col items-center gap-1 text-caption text-ink">
              <TeamMark
                name={meeting.awayAbbreviation}
                abbreviation={meeting.awayAbbreviation}
                logoUrl={logoFor(meeting.awayAbbreviation)}
                size="sm"
              />
              {meeting.awayAbbreviation}
            </span>
            {/* "24-17" with an ASCII hyphen, the leader in ink and the
                trailer muted at the same weight. A tie mutes neither. */}
            <span className="flex shrink-0 flex-col items-center gap-1">
              <span
                className="text-h2 tabular-nums"
                data-testid="matchup-meeting-score"
              >
                <span className={meetingScoreClass(meeting, "away")}>
                  {meeting.awayScore}
                </span>
                <span className="text-ink-secondary">-</span>
                <span className={meetingScoreClass(meeting, "home")}>
                  {meeting.homeScore}
                </span>
              </span>
              <span className="text-small text-ink-secondary">
                Home: {meeting.homeAbbreviation}
              </span>
            </span>
            <span className="flex min-w-0 flex-1 flex-col items-center gap-1 text-caption text-ink">
              <TeamMark
                name={meeting.homeAbbreviation}
                abbreviation={meeting.homeAbbreviation}
                logoUrl={logoFor(meeting.homeAbbreviation)}
                size="sm"
              />
              {meeting.homeAbbreviation}
            </span>
          </span>
        </div>
      ))}
    </GameSectionCard>
  );
};
